import React, { useRef, useState, useEffect, useMemo, useCallback, Suspense, Fragment } from "react";
import { Icon, Dropdown, Input, InputProps, DropdownProps, Menu, List, Segment } from "semantic-ui-react";
import { TPickerValue } from "../models/IHprPicker";
import { IPerson } from "../models/IPerson";
import { TResult, useBool, useLoading, useOnClickOutside } from "../../sg-core";

import "./_PersonSearch.less";

const defaultRenderPersonById = (person: IPerson["id"]) => <Fragment> {person} </Fragment>;
const defaultRenderSearchResult = (person: IPerson) => (
    <Menu.Item
        content={person.fullname}
    />
)
const noop = () => { };



interface PeoplePickerProps extends Omit<DropdownProps, "onChange" | "value"> {
    value?: TPickerValue;

    onAddPerson?: () => void;
    onEditPerson?: (person: IPerson) => void;
    onSearchPerson: (text: string) => Promise<TResult<IPerson[]>>;

    onChange: (value: TPickerValue) => void;

    renderSearchResult?: (person: IPerson) => JSX.Element;
    renderPersonLabel?: (id: IPerson["id"]) => JSX.Element;

    isMulti?: boolean;
};

const PeoplePicker: React.FC<PeoplePickerProps> = ({
    value,
    onChange,
    isMulti = false,
    clearable = true,

    onAddPerson = noop,
    onEditPerson = noop,
    onSearchPerson,

    renderPersonLabel = defaultRenderPersonById,
    renderSearchResult = defaultRenderSearchResult,

    ...dropdownProps
}) => {

    const t = (key: string) => key;
    const texts = {
        typeToSeach: t("Kirjoita hakeaksesi"),
        noResults: t("Ei tuloksia..."),
        addNew: t("Lisää uusi"),
        searchError: t("Haku virhe!"),
        selected: t("Valittuna")
    };

    const {
        loading: searching,
        loadError: searchError,
        startLoading,
        loadErrored,
        loadSuccess
    } = useLoading()
    //
    // Search
    //
    const timeoutRef = useRef<any>()
    const [query, setQuery] = useState<string>();
    const [searchResults, setSearchResults] = useState<IPerson[]>([]);
    const [isOpen, open, close] = useBool();

    const getValidValue = (value: TPickerValue) => isMulti ? Array.isArray(value) ? value : [] : value || null;
    const selected = getValidValue(value);

    const hasValue = Array.isArray(selected) ? selected.length : selected;
    const selectedCount = Array.isArray(selected) ? selected.length : selected ? 1 : 0;

    const loadOptions = async (query: string = "") => {
        const result = await onSearchPerson(query);
        if (result.isSuccess) {
            setSearchResults(result.value);
            loadSuccess();
        } else if (result.isFailure) {
            loadErrored(result.error.message)
        };
    };


    const _onInputChange: InputProps["onChange"] = (event, data) => {
        let newQuery = data.value;
        if (hasValue && isMulti && !query?.length) {
            newQuery = newQuery.slice(-1);
        }
        //@ts-ignore
        if ((event.key === "Backspace" || event.nativeEvent.inputType === "deleteContentBackward") && !query?.length) {
            return
        }
        if (!isOpen) {
            open()
        }
        setQuery(newQuery);
        startLoading();
        timeoutRef.current = clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => loadOptions(newQuery), 250);
    };

    const _onInputKeyDown: InputProps["onKeyDown"] = (event: any, data: any) => {
        if (event.key === " ") {
            _onInputChange(event, { ...data, value: event.target.value + " " }); // Fixes wierd bug, where spacebar is dosn't fire inputs onChange handler
            return
        } else if (event.key === "Backspace" && !query?.length) {
            if (hasValue) {
                if (isMulti) {
                    Array.isArray(value) && updateValue(value.splice(0, value.length - 1))
                } else {
                    updateValue(undefined)
                }
            }
        };
    };

    const closeResults = useCallback(() => {
        if (isOpen) {
            close();
            setQuery("");
        }
    }, [close, isOpen]);

    const _onInputFocus: InputProps["onFocus"] = async () => {
        if (!searchResults?.length) {
            loadOptions(" ");
        } else if (!query) {
            loadOptions(" ")
        }
        open();
    };


    const wrapperElement = useRef<any>();
    const inputElement = useRef<any>();

    useOnClickOutside(wrapperElement, closeResults);

    useEffect(() => {
        if (isOpen) {
            inputElement.current?.focus();
        }
    }, [isOpen]);


    const updateValue = (value: TPickerValue) => {
        onChange(value);
        if (!isMulti) { closeResults(); };

        if (query) { setQuery("") };

        const neutralizedValue = Array.isArray(value) ? value : value ? [value] : [];
        const unSelectedResults = searchResults.filter((res) => !neutralizedValue.includes(res.id));

        if (!unSelectedResults.length) { loadOptions(" ") }
    };

    const selectPerson = (itemId: number) => {
        if (isMulti) {
            if (!Array.isArray(selected)) throw Error("Selected is not array");
            const isSelected = selected.indexOf(itemId) > -1;
            updateValue(isSelected ? selected.filter((id) => id !== itemId) : [...selected, itemId]);
        } else {
            updateValue(itemId)
        };
    };

    const selectResult = (itemId: number) => {
        selectPerson(itemId);
        if (isMulti) {
            inputElement.current?.focus();
        }
    };

    const _clear = () => updateValue(null);



    const resultsWithoutSelected = useMemo(() => {
        return searchResults.filter((item) => (
            Array.isArray(selected) ? !selected.includes(item.id) : selected !== item.id
        ))
    }, [searchResults, selected]);


    return (
        <div className="PersonSearch-container">
            <div ref={wrapperElement} >

                <Dropdown
                    fluid
                    open={isOpen}
                    icon={null}
                    trigger={(
                        <div className="PersonSearch-value-container">
                            <Input
                                className="PersonSearch-input"
                                loading={searching}
                                icon="angle down"
                                ref={inputElement}
                                fluid
                                onFocus={_onInputFocus}
                                onChange={_onInputChange}
                                onKeyDown={_onInputKeyDown}
                                value={isOpen ? query : ""}
                                placeholder={!hasValue ? (
                                    isOpen ? texts.typeToSeach : dropdownProps.placeholder || texts.typeToSeach
                                ) : undefined}
                            />

                            {clearable && hasValue ? (
                                <div className="PersonSearch-clear">
                                    <Icon
                                        name="times"
                                        onClick={_clear}
                                    />
                                </div>
                            ) : null}

                            {!isOpen && hasValue ? (
                                <div className="PersonSearch-selected">
                                    {!Array.isArray(selected) && selected ? (
                                        renderPersonLabel(selected)
                                    ) : isMulti ? (
                                        `Valittuna ${selectedCount} henkilö`
                                    ) : null}
                                </div>
                            ) : null}

                        </div>
                    )}
                    {...dropdownProps}
                >
                    <Dropdown.Menu className="PersonSearch-dropdown-menu">
                        <Suspense fallback={<div />}>
                            {searchError && (
                                <Segment basic textAlign="center">
                                    <span style={{ color: "red", minWidth: "300px" }} > {texts.searchError} </span>
                                </Segment>
                            )}

                            {searching ? (
                                <Menu.Item disabled style={{ minWidth: "300px" }}> Haetaan...  </Menu.Item>
                            ) : searchResults?.length ? (
                                <Fragment>
                                    {searchResults.map((person) => (
                                        <Menu.Item
                                            key={person.id}
                                            onClick={() => selectResult(person.id)}
                                            children={renderSearchResult(person)}
                                        />
                                    ))}
                                    {onAddPerson && (
                                        <Menu.Item icon="plus" content={texts.addNew} onClick={onAddPerson} />
                                    )}
                                </Fragment>
                            ) : <Fragment>
                                <Menu.Item disabled>{texts.noResults}  </Menu.Item>
                                {onAddPerson && (
                                    <Menu.Item icon="plus" content={texts.addNew} onClick={onAddPerson} />
                                )}
                            </Fragment>
                            }
                        </Suspense>
                    </Dropdown.Menu>
                </Dropdown>
            </div>


            {isMulti && Array.isArray(selected) && selected.length ? (
                <List className="PersonSearch-list">
                    {selected.map((personId) => (
                        <Suspense fallback={<div />}>
                            <List.Item key={personId}> {renderPersonLabel(personId)} </List.Item>
                        </Suspense>
                    ))}
                </List>
            ) : null}
        </div>
    );
};


export default PeoplePicker;
